// src/api/BroadcastFormReportAPI.ts

import {
  IBroadcastFormMap,
  IEnterpriseBroadcastFormGeneratorMap,
  IBroadcastFormQuestionsByFormTypeMap,
  IBroadcastFormResponseFormatMap,
  IBroadcastFormResponseOptionsMap,
  IBroadcastFormRecipientResponseReportRow,
  IBroadcastFormReportGroup,
  IPageResult,
} from 'types/BroadcastForm'
import API, { AxiosRequestOptions } from './API'

// -------------------------
// Generators
// -------------------------

export const getBroadcastFormReportGenerators = async (
  options?: AxiosRequestOptions,
): Promise<IEnterpriseBroadcastFormGeneratorMap> => {
  const response = await API.get<IEnterpriseBroadcastFormGeneratorMap>(
    '/api/v2/BroadcastForms/Report/Generators',
    options,
  )
  return response.data
}

// -------------------------
// Forms (report scope)
// -------------------------

export const getBroadcastFormReportForms = async (
  options?: AxiosRequestOptions,
): Promise<IBroadcastFormMap> => {
  const response = await API.get<IBroadcastFormMap>(
    '/api/v2/BroadcastForms/Report/Forms',
    options,
  )
  return response.data
}

// -------------------------
// Questions (by FormType)
// -------------------------

export const getBroadcastFormReportQuestions = async (
  options?: AxiosRequestOptions,
): Promise<IBroadcastFormQuestionsByFormTypeMap> => {
  const response = await API.get<IBroadcastFormQuestionsByFormTypeMap>(
    '/api/v2/BroadcastForms/Report/Questions',
    options,
  )
  return response.data
}

// -------------------------
// Response formats
// -------------------------

export const getBroadcastFormReportResponseFormats = async (
  options?: AxiosRequestOptions,
): Promise<IBroadcastFormResponseFormatMap> => {
  const response = await API.get<IBroadcastFormResponseFormatMap>(
    '/api/v2/BroadcastForms/Report/ResponseFormats',
    options,
  )
  return response.data
}

// -------------------------
// Response options
// -------------------------

export const getBroadcastFormReportResponseOptions = async (
  options?: AxiosRequestOptions,
): Promise<IBroadcastFormResponseOptionsMap> => {
  const response = await API.get<IBroadcastFormResponseOptionsMap>(
    '/api/v2/BroadcastForms/Report/ResponseOptions',
    options,
  )
  return response.data
}

// -------------------------
// Groups (for filtering)
// -------------------------

export const getBroadcastFormReportGroups = async (
  options?: AxiosRequestOptions,
): Promise<IBroadcastFormReportGroup[]> => {
  const response = await API.get<IBroadcastFormReportGroup[]>(
    '/api/v2/BroadcastForms/Report/Groups',
    options,
  )
  return response.data
}

// -------------------------
// Paged recipient responses
// -------------------------

export type GetRecipientResponsesParams = {
  fromUtc?: string // ISO string in UTC
  toUtc?: string // ISO string in UTC
  page?: number
  pageSize?: number
}

export const getBroadcastFormRecipientResponsesPage = async (
  params: GetRecipientResponsesParams,
  options?: AxiosRequestOptions,
): Promise<IPageResult<IBroadcastFormRecipientResponseReportRow>> => {
  const {
    fromUtc, toUtc, page = 1, pageSize = 1000,
  } = params

  const queryParts: string[] = []

  if (fromUtc) {
    queryParts.push(`fromUtc=${encodeURIComponent(fromUtc)}`)
  }

  if (toUtc) {
    queryParts.push(`toUtc=${encodeURIComponent(toUtc)}`)
  }

  queryParts.push(`page=${page}`)
  queryParts.push(`pageSize=${pageSize}`)

  const queryString = queryParts.length > 0 ? `?${queryParts.join('&')}` : ''

  const response = await API.get<IPageResult<IBroadcastFormRecipientResponseReportRow>>(
    `/api/v2/BroadcastForms/Report/RecipientResponses${queryString}`,
    options,
  )

  return response.data
}
